import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

const SHORT_LINK_API = "https://f2qq5dfp52.execute-api.ap-south-1.amazonaws.com/default/create_short_link";

/**
 * /r/:code — resolves a short link IN-APP.
 *
 * Why: the old flow 302-redirected the browser to the stored long URL.
 * Route URLs carry the whole route base64-encoded in ?rdata=, and big routes
 * exceed CloudFront's 8,192-byte request-URI cap → 414 before the app loads.
 *
 * Here the long URL arrives as JSON and we navigate CLIENT-SIDE
 * (react-router), so it never travels through CloudFront as a request URI.
 * SharedMapPage reads ?rdata= from location exactly as before — unchanged.
 *
 * NOTE: /b/ booking links keep the CloudFront-redirect path; only /r/ lands here.
 */
const ShortLinkResolver = () => {
    const { code } = useParams();
    const navigate = useNavigate();
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const resolve = async () => {
            try {
                let resp = await fetch(`${SHORT_LINK_API}?code=${encodeURIComponent(code)}`);
                if (resp.status === 404 || resp.status === 405) {
                    // Route may be POST-only on API Gateway — fall back to POST resolve
                    resp = await fetch(SHORT_LINK_API, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ action: 'resolve', code }),
                    });
                }

                const payload = await resp.json().catch(() => ({}));
                if (cancelled) return;

                if (resp.status === 410) {
                    setError('This shared link has expired. Ask your friend to share the route again!');
                    return;
                }
                const longUrl = payload?.data?.long_url;
                if (!resp.ok || !longUrl) {
                    setError('This link could not be found. It may have expired or been mistyped.');
                    return;
                }

                const url = new URL(longUrl, window.location.origin);
                if (url.origin === window.location.origin) {
                    // Internal destination: client-side navigation — the long URL
                    // becomes app state + address-bar text, never an HTTP request.
                    navigate(url.pathname + url.search + url.hash, { replace: true });
                } else {
                    // External destination (defensive): a real redirect is fine there.
                    window.location.replace(longUrl);
                }
            } catch (e) {
                if (!cancelled) setError('Could not open this link right now. Please try again in a moment.');
            }
        };

        resolve();
        return () => { cancelled = true; };
    }, [code, navigate]);

    return (
        <div style={{
            minHeight: '70vh', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 14,
            fontFamily: "'Inter', sans-serif", padding: '0 24px', textAlign: 'center',
        }}>
            {!error ? (
                <>
                    <span style={{
                        width: 26, height: 26, borderRadius: '50%',
                        border: '3px solid #FFD8C2', borderTopColor: '#FF4E01',
                        animation: 'rc-resolve-spin 0.8s linear infinite',
                    }} />
                    <style>{'@keyframes rc-resolve-spin { to { transform: rotate(360deg); } }'}</style>
                    <p style={{ color: '#475569', fontSize: 15, margin: 0 }}>Opening your shared route…</p>
                </>
            ) : (
                <>
                    <p style={{ color: '#0F172A', fontSize: 17, fontWeight: 600, margin: 0 }}>{error}</p>
                    <Link to="/" style={{
                        color: '#FF4E01', fontWeight: 600, fontSize: 14.5,
                        textDecoration: 'none', border: '1px solid #FF4E01',
                        borderRadius: 9999, padding: '9px 20px',
                    }}>
                        Search routes on RouteChef
                    </Link>
                </>
            )}
        </div>
    );
};

export default ShortLinkResolver;
